import { useState, useRef, useEffect, useMemo } from 'react';
import { debounce } from 'lodash-es';
import bem from '../../../utils/bem';
import Icon from '../../Atoms/Icon/Icon';
import Modal from '../Modal';
import Tile from '../../Atoms/ProductAttributeTile';
import sortProductAttributes from '../../../utils/sortProductAttributes';
import damImageSwatchFormatter from '../../../utils/damImageSwatchFormatter';

let resizeObserver;

const ProductAttributeGroup = (props) => {
  const {
    label,
    machineName,
    items,
    type,
    disabledMessage,
    enableMoreButtonClick,
    helpText,
    keyProp,
    enableCustomize,
    customFixedItemsLength,
    triggerVariant,
    activeAttributes,
    filteredList,
    enableCustomColor = false,
  } = props;
  const typeUsed = type || 'text';
  const [itemsExpanded, setItemsExpanded] = useState(false);
  const [showItems, setShowItems] = useState(9999);
  const [moreItems, setMoreItems] = useState(0);
  const [blockWidth, setBlockWidth] = useState(0);
  const [itemsFiltered, setItemsFiltered] = useState(items);
  const wrapRef = useRef([]);
  const baseClass = 'product-attribute';

  let levelInfo = null;
  let extraTargetClass = '';
  if (
    !!items[0]?.heroCarousel?.length &&
    items[0]?.groupedBy === 'marketingColor'
  ) {
    levelInfo =
      'Click on the color swatches below to see the selected products in the gallery.';
  }
  if (
    !!items[0]?.heroCarousel?.length &&
    items[0]?.groupedBy === 'nominalThickness'
  ) {
    levelInfo =
      'Click on the thickness options below to see the selected products in the gallery.';
    extraTargetClass = 'nominal_thickness';
  }
  const enableMoreButtonClickUsed =
    typeof enableMoreButtonClick === 'undefined' ? true : enableMoreButtonClick;

  const moveCustomColor = () => {
    if (enableCustomColor && itemsFiltered[showItems]) {
      let idx = 0;
      itemsFiltered.forEach((item, i) => {
        if (item.field_is_custom) {
          idx = i;
        }
      });
      if (idx) {
        const custom = itemsFiltered.splice(idx, 1);
        const itemsFilteredNew = [...itemsFiltered];
        itemsFilteredNew.splice(showItems - 1, 0, custom[0]);
        setItemsFiltered(itemsFilteredNew);
      }
    }
  };

  useEffect(() => {
    const itemsFilteredValue =
      typeUsed === 'image' ? items.filter((item) => item?.img) : items;
    setItemsFiltered(itemsFilteredValue);
    moveCustomColor();
  }, [items]);

  const calculateShowItems = () => {
    if (typeof customFixedItemsLength === 'number') {
      setShowItems(customFixedItemsLength);
      return;
    }

    const groupedByLines = {};

    wrapRef.current
      .querySelectorAll('.product-attribute__item')
      .forEach((item) => {
        if (item) {
          const el = item.getBoundingClientRect();
          groupedByLines[`idx-${el.y}`] = groupedByLines[`idx-${el.y}`] || [];
          groupedByLines[`idx-${el.y}`].push(el);
        }
      });
    const groupedByLinesKeys = Object.keys(groupedByLines);
    if (groupedByLinesKeys[1]) {
      const backOffset = typeUsed === 'image' && enableCustomize ? 2 : 1;
      const showItemsCalculated =
        groupedByLines[`${groupedByLinesKeys[0]}`].length - backOffset;
      setShowItems(showItemsCalculated || 1);
    }
  };

  useEffect(() => {
    resizeObserver = new ResizeObserver(
      debounce((entries) => {
        const { borderBoxSize } = entries[0];
        const { inlineSize } = borderBoxSize[0];
        if (blockWidth !== inlineSize) {
          setBlockWidth(inlineSize);
        }
      }, 333),
    );
    resizeObserver.observe(wrapRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    moveCustomColor();
  }, [showItems, blockWidth]);

  useMemo(() => {
    const backOffset = typeUsed === 'image' ? showItems + 1 : showItems;
    setMoreItems(itemsFiltered.length - backOffset);
  }, [showItems, itemsFiltered]);

  useEffect(() => {
    setShowItems(9999);
    // Wait for button classes to be updates to calculate getBoundingClientRect.
    setTimeout(calculateShowItems, 33);
  }, [blockWidth]);

  useEffect(() => {
    calculateShowItems();
  }, [customFixedItemsLength]);
  const moreButton = () => {
    return moreItems >= 0 && itemsFiltered[`${showItems}`] ? (
      <button
        className={bem('item', ['more', typeUsed], baseClass)}
        onClick={() => enableMoreButtonClickUsed && setItemsExpanded(true)}
      >
        {typeUsed === 'image' ? (
          <>
            <img
              src={damImageSwatchFormatter(itemsFiltered[`${showItems}`].img)}
              alt={itemsFiltered[`${showItems}`].label}
              loading="lazy"
            />
            <span>+{moreItems === 0 ? moreItems + 1 : moreItems}</span>
          </>
        ) : (
          `+${moreItems}`
        )}
      </button>
    ) : (
      ''
    );
  };
  const renderTile = (item, key) => (
    <Tile
      item={item}
      activeAttributes={activeAttributes}
      filteredList={filteredList}
      typeUsed={typeUsed}
      disabledMessage={disabledMessage}
      baseClass={baseClass}
      triggerVariant={triggerVariant}
      extraClasses={
        key >= showItems && !itemsExpanded ? ['hidden'] : [extraTargetClass]
      }
    />
  );

  const lessButton = (
    <div className="w-full">
      <button
        className={bem('item', ['less'], baseClass)}
        onClick={() => setItemsExpanded(false)}
      >
        {Drupal.t('Show less')}
      </button>
    </div>
  );

  const customizeButton = typeUsed === 'image' && (
    <button className={bem('item', ['customize', typeUsed], baseClass)}>
      <span className={'visually-hidden '}>{Drupal.t('Customize')}</span>
      <Icon iconName="gear" />
    </button>
  );

  const modalId = label ? `${keyProp}-${label.replace(' ', '')}` : keyProp;
  const ariaLabelId = window.crypto.randomUUID();

  sortProductAttributes(itemsFiltered);

  return (
    <div className={bem(baseClass)} ref={wrapRef}>
      {label && (
        <div
          className={bem('heading', [], baseClass, ['flex', 'justify-between'])}
        >
          {itemsFiltered.length !== 0 && (
            <div className={bem('label', [], baseClass)}>
              <b id={ariaLabelId}>{label}</b> ({itemsFiltered.length})
            </div>
          )}
          {helpText && (
            <div className={bem('variant-description', [], baseClass)}>
              <button
                className={bem('variant-description-btn', [], baseClass, [
                  'js-modal-open',
                ])}
                data-modal-id={modalId}
              >
                <span className={'visually-hidden'}>
                  {Drupal.t('Customize')}
                </span>
                <Icon iconName="question-mark" />
              </button>
              <Modal modal_id={modalId} modal_modifiers={['product-attr']}>
                <div dangerouslySetInnerHTML={{ __html: helpText }} />
              </Modal>
            </div>
          )}
        </div>
      )}
      {levelInfo && (
        <div className={bem('info', [], 'product-availability')}>
          {Drupal.t(levelInfo)}
        </div>
      )}

      <ul
        aria-labelledby={ariaLabelId}
        className={bem('items', [], baseClass, ['flex', 'flex-wrap'])}
      >
        {itemsFiltered.map((item, key) => {
          return !(key === showItems && !itemsExpanded) && item ? (
            <li
              key={`group-${machineName}_attr_${item.groupedBy}-${item.value}-${key}`}
            >
              {renderTile(item, key)}
            </li>
          ) : (
            <li key={`group-${machineName}_attr_More-Btn`}>{moreButton()}</li>
          );
        })}
        {enableCustomize && customizeButton}
        {itemsExpanded && lessButton}
      </ul>
    </div>
  );
};

export default ProductAttributeGroup;
